import { defineStore } from 'pinia';
import { deleteFile, getFolderDetail, moveFile, renameFile } from '@/api/photo-space';

type ModeType = 'manage' | 'select' | 'goods';

interface FileListState {
  mode: ModeType
  showFolder: boolean
  limit: number
  folderList: API.FolderDetailFolder[]
  fileList: API.FolderDetailFile[]
  fileListLoading: boolean
  selectedFile: number[]
  isSearchMode: boolean
  searchKey: string
  pId: number
  /** 当前文件夹名称（用于顶部展示） */
  currentFolderName: string
  /** 导航栈：存储上一级目录的 { id, name }，用于「返回上一层」 */
  navStack: { id: number, name: string }[]
}

export const useFileListStore = defineStore('photo-file-list', {
  state: (): FileListState => ({
    mode: 'manage',
    showFolder: true,
    limit: 0,
    folderList: [],
    fileList: [],
    fileListLoading: false,
    selectedFile: [],
    isSearchMode: false,
    searchKey: '',
    pId: 0,
    currentFolderName: '',
    navStack: [],
  }),
  getters: {},
  actions: {
    async getFileList(pId: number, refresh: boolean = false) {
      // 持久缓存（1天）：根目录数据直接从缓存恢复，跳过API
      if (pId === 0) {
        // 已在根目录且有数据时跳过重复加载；从子目录返回时不命中此条件，会正常请求
        if (this.pId === 0 && this.fileList.length > 0) {
          return Promise.resolve();
        }
        const cached = this.loadRootCache();
        if (cached) {
          this.pId = cached.pId;
          this.fileList = cached.fileList;
          this.folderList = cached.folderList;
          return Promise.resolve();
        }
      }

      if (!refresh && this.pId === pId && this.fileList.length > 0) return Promise.resolve();
      this.isSearchMode = false;
      this.searchKey = '';
      this.fileListLoading = true;
      return getFolderDetail({ p_id: pId })
        .then((res) => {
          this.pId = pId;
          if (res) {
            this.fileList = res.file_list.map(item => ({
              ...item,
              itemType: item.file_name?.endsWith('.mp4') ? 'video' : 'image' as const,
            }));
            if (this.showFolder) {
              this.folderList = (res.folder_list || []).map(item => ({
                ...item,
                itemType: 'folder' as const,
              })).sort((a, b) => a.id - b.id);
            }
            // 根目录数据写入缓存
            if (pId === 0) {
              this.saveRootCache();
            }
          }
        })
        .finally(() => {
          this.fileListLoading = false;
        });
    },

    // ===== 根目录缓存（1天有效） =====
    initRootFromCache() {
      const cached = this.loadRootCache();
      if (cached) {
        this.pId = cached.pId;
        this.fileList = cached.fileList;
        this.folderList = cached.folderList;
      }
    },
    saveRootCache() {
      try {
        uni.setStorageSync('photo_root_cache', JSON.stringify({
          pId: this.pId,
          fileList: this.fileList,
          folderList: this.folderList,
          timestamp: Date.now(),
        }));
      }
      catch { /* 缓存写入失败不影响功能 */ }
    },
    loadRootCache(): { pId: number, fileList: any[], folderList: any[] } | null {
      try {
        const raw = uni.getStorageSync('photo_root_cache');
        if (!raw) return null;
        const data = JSON.parse(raw);
        // 1 天过期
        if (Date.now() - data.timestamp > 86400000) {
          uni.removeStorageSync('photo_root_cache');
          return null;
        }
        return data;
      }
      catch { return null; }
    },
    clearFileList(): void {
      this.fileList.length = 0;
    },
    async deleteFileFun(id: number) {
      return deleteFile({ file_id: id });
    },
    async moveFileFun(id: number, pId: number) {
      return moveFile({ file_id: id, p_id: pId });
    },
    async renameFileFun(id: number, name: string) {
      return renameFile({ file_id: id, file_name: name });
    },
    setSelectFile(id: number) {
      const item = this.fileList.find(item => item.id === id);
      if (!item) return;
      if (this.selectedFile.includes(id)) {
        this.selectedFile = this.selectedFile.filter(item => item !== id);
      }
      else {
        this.selectedFile = [...this.selectedFile, id];
      }
    },
    clearSelectFile() {
      this.selectedFile.length = 0;
    },
    setPId(pId: number): void {
      this.pId = pId;
    },
    setMode(mode: ModeType): void {
      this.mode = mode;
    },

    // ===== 导航栈（替代文件夹树） =====
    /** 进入子文件夹前，保存当前目录信息以便返回 */
    pushNav(id: number, name: string) {
      this.navStack.push({ id, name });
    },
    /** 返回上一级，弹出栈顶目录信息 */
    popNav(): { id: number, name: string } {
      return this.navStack.pop() || { id: 0, name: '' };
    },
    /** 重置导航栈（进入素材空间首页时调用） */
    resetNav() {
      this.navStack = [];
      this.currentFolderName = '';
    },
  },
});
